/**
 * @voltxampere/engine — Integration method dispatcher
 *
 * Routes a `(method, history)` pair to the matching BE / TRAP /
 * Gear-2 companion. Two responsibilities:
 *
 *  1. **Method selection.** `"BE"` → BE companion, `"TRAP"` → TRAP
 *     companion, `"Gear-2"` → Gear-2 companion.
 *  2. **First-step BE fallback for Gear-2.** Gear-2 needs `x(t-2h)`,
 *     which doesn't exist for the very first transient step. When
 *     `VCPrevPrev` / `ILPrevPrev` is `null`, the dispatcher quietly
 *     falls back to BE for that step — the integrator promotes to
 *     full Gear-2 from step 2 onward when the second history point
 *     becomes available.
 *
 * Dispatching here (rather than in each stamp) keeps the per-stamp
 * code one-shape and isolates the bootstrapping policy in a single
 * audit-friendly file. Sprint 7+ may add higher-order BDFs that
 * also bootstrap from BE; the same fallback handles them.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { ContractViolationError } from "@voltxampere/shared";
import { capacitorBE, inductorBE } from "./backward-euler.js";
import { capacitorTRAP, inductorTRAP } from "./trapezoidal.js";
import { capacitorGear2, inductorGear2 } from "./gear-2.js";
/**
 * Pick the capacitor companion model for the requested method,
 * falling back to BE for Gear-2 when no `t-2h` history exists.
 */
export function selectCapacitorCompanion(input) {
    const { method, C, h, hPrev, VCPrev, ICPrev, VCPrevPrev } = input;
    switch (method) {
        case "BE":
            return capacitorBE(C, h, VCPrev);
        case "TRAP":
            return capacitorTRAP(C, h, VCPrev, ICPrev);
        case "Gear-2":
            if (VCPrevPrev === null) {
                // First-step bootstrap: no x(t-2h), use BE this iteration.
                return capacitorBE(C, h, VCPrev);
            }
            return capacitorGear2(C, h, hPrev, VCPrev, VCPrevPrev);
        default: {
            // Defensive: never reachable while IntegrationMethod stays a
            // closed union, but the TS exhaustiveness check would fall
            // through silently otherwise.
            const _exhaustive = method;
            throw new ContractViolationError(`selectCapacitorCompanion: unknown integration method "${String(_exhaustive)}"`);
        }
    }
}
/** Inductor counterpart — see {@link selectCapacitorCompanion}. */
export function selectInductorCompanion(input) {
    const { method, L, h, hPrev, ILPrev, VLPrev, ILPrevPrev } = input;
    switch (method) {
        case "BE":
            return inductorBE(L, h, ILPrev);
        case "TRAP":
            return inductorTRAP(L, h, ILPrev, VLPrev);
        case "Gear-2":
            if (ILPrevPrev === null) {
                return inductorBE(L, h, ILPrev);
            }
            return inductorGear2(L, h, hPrev, ILPrev, ILPrevPrev);
        default: {
            const _exhaustive = method;
            throw new ContractViolationError(`selectInductorCompanion: unknown integration method "${String(_exhaustive)}"`);
        }
    }
}
//# sourceMappingURL=dispatcher.js.map